/**
 * "A new version is ready — Update" for the installed launcher. iOS keeps a Home Screen
 * app's page alive for days, so a deploy only shows up after a reload. Two signals,
 * one pill (docs/architecture.md §14):
 *  - /version.json (written by vite.config.ts, `no-cache`) names a build other than the
 *    `__BUILD_ID__` this page was built with; checked on mount, whenever the page becomes
 *    visible, and every 15 minutes while it stays visible;
 *  - a new service worker installed (its app shell is cached) or took over this page (lib/sw.ts).
 * The launcher is served from the worker's cached shell (architecture §6), so a newer
 * /version.json also asks the worker to update, and "Update" reloads only once the new
 * worker is in control. Nothing reloads on its own: the member taps the pill.
 */
import { useEffect, useSyncExternalStore } from 'react';
import { onServiceWorkerUpdate, registerServiceWorker, whenNewWorkerControls } from './sw';

const CHECK_EVERY_MS = 15 * 60_000;

type State = { remoteBuild: string | null; workerUpdated: boolean; dismissed: boolean };

let state: State = { remoteBuild: null, workerUpdated: false, dismissed: false };
const listeners = new Set<() => void>();

function set(next: Partial<State>): void {
  state = { ...state, ...next };
  for (const l of listeners) l();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

async function checkVersion(): Promise<void> {
  try {
    const res = await fetch('/version.json', { cache: 'no-store' });
    if (!res.ok) return;
    const body = (await res.json()) as { build?: unknown };
    const build = typeof body.build === 'string' ? body.build : null;
    if (!build || build === __BUILD_ID__ || build === state.remoteBuild) return;
    // A newer build than the one dismissed earlier asks again.
    set({ remoteBuild: build, dismissed: false });
    // Installs the new shell in the background, so "Update" is quick.
    void registerServiceWorker().then((r) => r?.update().catch(() => {}));
  } catch {
    // Offline or a half-deployed origin: try again on the next check.
  }
}

/** Starts the checks (while mounted) and tells whether to show the pill. */
export function useUpdateAvailable(): boolean {
  useEffect(() => {
    const check = () => {
      if (document.visibilityState === 'visible') void checkVersion();
    };
    // Back/forward cache restores (iOS swipe back from an app) count as coming to the foreground.
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) check();
    };
    check();
    document.addEventListener('visibilitychange', check);
    window.addEventListener('pageshow', onPageShow);
    const timer = window.setInterval(check, CHECK_EVERY_MS);
    const stopWorker = onServiceWorkerUpdate(() => set({ workerUpdated: true, dismissed: false }));
    return () => {
      document.removeEventListener('visibilitychange', check);
      window.removeEventListener('pageshow', onPageShow);
      window.clearInterval(timer);
      stopWorker();
    };
  }, []);
  const current = useSyncExternalStore(subscribe, () => state);
  return (current.remoteBuild !== null || current.workerUpdated) && !current.dismissed;
}

export function dismissUpdate(): void {
  set({ dismissed: true });
}

const within = (ms: number, work: Promise<unknown>) => Promise.race([work, new Promise((done) => setTimeout(done, ms))]);

/**
 * Lets the service worker pick up a new sw.js and waits for it to take control (both
 * bounded, so an offline phone still reloads), then reloads.
 */
export async function applyUpdate(): Promise<void> {
  const registration = await registerServiceWorker();
  if (registration) {
    await within(3000, registration.update().catch(() => {}));
    await within(10_000, whenNewWorkerControls(registration));
  }
  window.location.reload();
}
